import {Button, useDisclosure} from "@heroui/react"
import {IconFilter} from "@tabler/icons-react"
import {FilterIndicator} from "@/components/common/Indicators"
import {OptionMap, OptionTypes} from "@/components/archive/Filtering/utils"
import FiltersDrawerMenu from "@/components/archive/Filtering/FiltersDrawerMenu"

interface FilterBarProps {
  filters: OptionMap[]
  onFiltersChange: (filters: OptionMap[]) => void
  onClearAll: () => void
}

const FilterBar = ({filters, onFiltersChange, onClearAll}: FilterBarProps) => {
  const {
    isOpen: isFilterDrawerOpen,
    onOpen: onFilterDrawerOpen,
    onOpenChange: onFilterDrawerOpenChange,
  } = useDisclosure()

  /**
   * Validates and processes filters before submitting them to the parent component
   * Ensures only valid filters with complete data are used
   */
  function submitFilters(filters: OptionMap[]) {
    // Only set valid filters
    const validFilters = filters.filter((f) => {
      if (f.type === "contributors") {
        return (
          Array.isArray(f.value.contributors) && f.value.contributors.length > 0
        )
      }
      if (f.type === "date_range") {
        return f.value.start && f.value.end
      }
      return false
    })

    // Notify parent about filter changes
    onFiltersChange(validFilters)
  }

  return (
    <div className="z-20 flex h-8 flex-row justify-end">
      <Button
        className=""
        variant={filters.length > 0 ? "solid" : "bordered"}
        color="primary"
        size="sm"
        radius="full"
        onPress={onFilterDrawerOpen}
      >
        <IconFilter className="h-4 w-4" />
        <p>{filters.length > 0 ? `Filtered (${filters.length})` : "All"}</p>
      </Button>
      {filters.length > 0 && (
        <div className="ml-2 flex flex-row items-center gap-2">
          <div className="flex flex-row gap-0">
            {Array.from(
              filters.reduce((acc, filter) => {
                acc.set(filter.type, (acc.get(filter.type) || 0) + 1)
                return acc
              }, new Map<string, number>())
            ).map(([type, count]: [string, number], index: number) => {
              return (
                <FilterIndicator
                  key={type}
                  type={type as OptionTypes}
                  text={String(count)}
                  containerClass={index > 0 ? "translate-x-[-0.5rem]" : ""}
                />
              )
            })}
          </div>
          <button
            className="text-xs text-red-500 hover:underline"
            onClick={onClearAll}
          >
            Clear All
          </button>
        </div>
      )}
      <FiltersDrawerMenu
        isOpen={isFilterDrawerOpen}
        onOpenChange={onFilterDrawerOpenChange}
        submitFilters={submitFilters}
        currentFilters={filters}
      />
    </div>
  )
}

export default FilterBar
